import React, { useContext, useEffect, useState, ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import lscache from "lscache";

// Components
import AcceptCookies from "../Components/GlobalUI/AcceptCookies";
import Footer from "../Components/GlobalUI/Footer";
import Toast from "../Components/UI/Toasts/Toast";
import ScrollToTop from "../Components/GlobalUI/ScrollToTop";
import MarketingScripts from "../Components/Head/MarketingScripts";
import Header from "../Components/GlobalUI/Header";
import FrontPageHeader from "../Components/GlobalUI/FrontPageHeader";
import NotificationBar from "../Components/GlobalUI/NotificationBar";
import DataDrivenPopup from "../Components/Popups/DataDrivenPopup";
import DeleteVideoCommentPopup from "../Components/UI/Comments/DeleteVideoCommentPopup";

// Context
import { UserAuthenticationContext } from "../Context/UserAuthenticationContext";
import { GlobalInterfaceContext } from "../Context/GlobalInterfaceContext";

// Constant
import { RoutePaths, URLsWithoutUI, URLsWithNotifyBar } from "../Constants/RoutePaths";

// Utility
import { ShowWelcomeToast } from "../Helpers/Notifications";
import {getAffiliateReferral} from "../Helpers/AffiliateReferral";

const POPUP_DISMISS_KEY = "site_popup_dismissed_until";
const WEEK_IN_MINUTES = 7 * 24 * 60;

const RootLayout = (props: { children?: ReactNode }) => {
    const {
            hasNotificationBar,
            isDarkMode,
            mainMenuDesktopMode
        } = useContext(GlobalInterfaceContext),
        authCtx = useContext(UserAuthenticationContext),
        location = useLocation(),
        [isLoaded, setIsLoaded] = useState<boolean>(false),
        currentLocation = useLocation(),
        isFrontPage = currentLocation.pathname === RoutePaths.Root,
        shouldDisplayGlobalUI = !URLsWithoutUI.some((path) => path.test(currentLocation.pathname)),
        shouldDisplayNotifyBar = URLsWithNotifyBar.some((path) => path.test(currentLocation.pathname)),

        // Marketing popup gate
        [popupOpen, setPopUpOpen] = useState(false);

    useEffect(() => {
        getAffiliateReferral(location.search);
    }, [location.search]);

    useEffect(() => {
        if (isLoaded) {
            ShowWelcomeToast(authCtx?.userData?.first_name, location);
            return;
        }
        const t = setTimeout(() => setIsLoaded(true), 500);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authCtx?.userData]);

    useEffect(() => {
        // Open popup only if not dismissed within the last 7 days
        const dismissed = !!lscache.get(POPUP_DISMISS_KEY);
        if (!dismissed) setPopUpOpen(true);
    }, []);

    useEffect(() => {
        // Always settle startup auth, including expired or malformed saved sessions.
        // Token presence starts the loading state; token validity must not skip cleanup.
        void authCtx.bootstrapAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePopupClose = () => {
        setPopUpOpen(false);
        lscache.set(POPUP_DISMISS_KEY, 1, WEEK_IN_MINUTES); // value=1, TTL in minutes
    };

    return (
        <main
            role="main"
            className={
                `ickonic-root-layout
                ${
                    shouldDisplayNotifyBar && hasNotificationBar
                        ? "has-notification-bar"
                        : "notification-bar-hidden"
                }`
            }
        >
            <MarketingScripts />

            {/* Tracking Consent Banner */}
            <AcceptCookies />

            {/* Top notification bar */}
            {shouldDisplayGlobalUI && shouldDisplayNotifyBar && hasNotificationBar && (
                <NotificationBar isDarkMode={isDarkMode} />
            )}

            {/* Main Header */}
            {shouldDisplayGlobalUI && (
                isFrontPage
                    ? <FrontPageHeader />
                    : <Header shouldDisplayNotifyBar={shouldDisplayNotifyBar} />
            )}

            <div
                className={`container
              ${isFrontPage ? "has-front-page-header" : shouldDisplayGlobalUI ? "has-header" : "has-no-header"}
              ${isDarkMode ? "is-dark-mode" : "is-light-mode"}
              ${mainMenuDesktopMode ? "menu-is-desktop" : "menu-is-mobile"}
              ${authCtx.userData?.id ? "user-is-authenticated" : "user-not-authenticated"}
        `}
            >
                {/* Error wrapper passthrough */}
                {props?.children}

                {/* React Router Body */}
                <Outlet />
            </div>

            <DeleteVideoCommentPopup />

            {shouldDisplayGlobalUI && (
                <>
                    <Footer />
                    <Toast />
                    <ScrollToTop />
                    <DataDrivenPopup />
                </>
            )}
        </main>
    );
};

export default RootLayout;
